import { CustomView, CustomViewAccess, Partner, PlatformUser } from '@provision/entity-types';
import { gql, useMutation, useQuery } from '@provision/graphql-client';
import { useCallback, useMemo } from 'react';
import { isNullish } from 'remeda';
import { MAX_QUERY_LIMIT } from 'src/graphql/constants';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { ConditionInput, QueryVariables, Records } from 'src/graphql/types';

const CUSTOM_VIEW_ACCESS_FIELDS = gql`
  fragment CustomViewAccessFields on CustomViewAccess {
    id
    isFavorite
    userId
    customViewId
    customViewAccessId
    partnerId
    sortOrder
    createdAt
  }
`;

const GET_CUSTOM_VIEW_ACCESS_QUERY = gql`
  query GetCustomViewAccess($params: QueryParams) {
    getCustomViewAccess(queryParams: $params) {
      records {
        ...CustomViewAccessFields
      }
    }
  }
  ${CUSTOM_VIEW_ACCESS_FIELDS}
`;

const CREATE_CUSTOM_VIEW_ACCESS_MUTATION = gql`
  mutation CreateCustomViewAccess(
    $userId: String
    $isFavorite: Boolean
    $customViewId: String
    $partnerId: Int
    $sortOrder: Int
  ) {
    createCustomViewAccess(
      data: {
        userId: $userId
        sortOrder: $sortOrder
        isFavorite: $isFavorite
        customViewId: $customViewId
        partnerId: $partnerId
      }
    ) {
      ...CustomViewAccessFields
    }
  }
  ${CUSTOM_VIEW_ACCESS_FIELDS}
`;

const DELETE_CUSTOM_VIEW_ACCESS_MUTATION = gql`
  mutation DeleteCustomViewAccess($customViewAccessId: String!) {
    deleteCustomViewAccess(
      conditions: [
        { attribute: "customViewAccessId", operator: "=", value: $customViewAccessId }
      ]
    ) {
      ...CustomViewAccessFields
    }
  }
  ${CUSTOM_VIEW_ACCESS_FIELDS}
`;

const UPDATE_CUSTOM_VIEW_ACCESS_MUTATION = gql`
  mutation UpdateCustomViewAccess(
    $customViewAccessId: String!
    $isFavorite: Boolean
    $sortOrder: Int
  ) {
    updateCustomViewAccess(
      conditions: [
        { attribute: "customViewAccessId", operator: "=", value: $customViewAccessId }
      ]
      data: { isFavorite: $isFavorite, sortOrder: $sortOrder }
    ) {
      ...CustomViewAccessFields
    }
  }
  ${CUSTOM_VIEW_ACCESS_FIELDS}
`;

export interface GetCustomViewAccess {
  getCustomViewAccess: Records<CustomViewAccess>;
}

export interface CreateCustomViewAccess {
  createCustomViewAccess: CustomViewAccess[];
}

export interface UpdateCustomViewAccess {
  updateCustomViewAccess: CustomViewAccess[];
}

export interface DeleteCustomViewAccess {
  deleteCustomViewAccess: CustomViewAccess[];
}

export const useCustomViewAccess = () => {
  const { user } = useCurrentUserProfile();

  const filters = useMemo(
    () => [
      {
        attribute: 'userId',
        operator: '=',
        value: user?.userId!,
      },
    ],
    [user?.userId],
  );

  const { data } = useQuery<GetCustomViewAccess, QueryVariables>(
    GET_CUSTOM_VIEW_ACCESS_QUERY,
    {
      skip: isNullish(user?.userId),
      variables: {
        params: {
          filters,
          limit: MAX_QUERY_LIMIT,
          sorting: { attribute: 'createdAt', desc: true },
        },
      },
    },
  );

  const operations = useCustomViewAccessOperations({ filters });

  const customViewAccessesMap = useMemo(
    () => new Map(data?.getCustomViewAccess?.records.map((i) => [i.customViewId, i])),
    [data?.getCustomViewAccess?.records],
  );

  return {
    ...operations,
    customViewAccesses: data?.getCustomViewAccess?.records || [],
    customViewAccessesMap,
  };
};

function useCustomViewAccessOperations({ filters }: { filters: ConditionInput[] }) {
  const { user } = useCurrentUserProfile();

  const [createCustomViewAccess, { loading: loadingCreateCustomViewAccess }] =
    useMutation<CreateCustomViewAccess>(CREATE_CUSTOM_VIEW_ACCESS_MUTATION, {
      update(cache, { data }) {
        if (!data?.createCustomViewAccess?.length) return;
        if (!data.createCustomViewAccess[0].userId) return;

        cache.updateQuery<GetCustomViewAccess, QueryVariables>(
          {
            query: GET_CUSTOM_VIEW_ACCESS_QUERY,
            variables: {
              params: {
                filters,
                limit: MAX_QUERY_LIMIT,
                sorting: { attribute: 'createdAt', desc: true },
              },
            },
          },
          (existingData) => {
            if (!existingData) return;

            return {
              getCustomViewAccess: {
                ...existingData.getCustomViewAccess,
                records: [
                  data.createCustomViewAccess[0],
                  ...existingData.getCustomViewAccess.records,
                ],
              },
            };
          },
        );
      },
    });

  const [deleteCustomViewAccess] = useMutation<DeleteCustomViewAccess>(
    DELETE_CUSTOM_VIEW_ACCESS_MUTATION,
    {
      update(cache, { data }) {
        if (!data?.deleteCustomViewAccess?.length) return;
        if (!data.deleteCustomViewAccess[0].userId) return;

        const id = cache.identify({
          __typename: 'CustomViewAccess',
          id: data.deleteCustomViewAccess[0].customViewAccessId,
        });

        cache.evict({ id });
        cache.gc();
      },
    },
  );

  const [updateCustomViewAccess, { loading: loadingUpdateCustomViewAccess }] =
    useMutation<UpdateCustomViewAccess>(UPDATE_CUSTOM_VIEW_ACCESS_MUTATION, {
      update(cache, { data }) {
        if (!data?.updateCustomViewAccess?.length) return;

        cache.updateQuery<GetCustomViewAccess, QueryVariables>(
          {
            query: GET_CUSTOM_VIEW_ACCESS_QUERY,
            variables: {
              params: {
                filters,
                limit: MAX_QUERY_LIMIT,
                sorting: { attribute: 'createdAt', desc: true },
              },
            },
          },
          (existingData) => {
            if (!existingData) return;

            return {
              getCustomViewAccess: {
                ...existingData.getCustomViewAccess,
                records: existingData.getCustomViewAccess.records.map((record) =>
                  record.customViewId === data.updateCustomViewAccess[0].customViewId
                    ? { ...record, ...data.updateCustomViewAccess[0] }
                    : record,
                ),
              },
            };
          },
        );
      },
    });

  const updateWithOptimisticResponse = useCallback(
    (
      customViewAccessId: string,
      data: Omit<CustomViewAccess, 'sortOrder'> & { sortOrder?: number | null },
    ) => {
      void updateCustomViewAccess({
        variables: {
          customViewAccessId,
          isFavorite: data?.isFavorite,
          sortOrder: data?.sortOrder,
        },
        optimisticResponse: {
          updateCustomViewAccess: [
            {
              ...data,
              sortOrder: data?.sortOrder ?? new Date(data.createdAt).getTime(),
              customViewAccessId,
            },
          ],
        },
      });
    },
    [updateCustomViewAccess],
  );

  const createWithOptimisticResponse = useCallback(
    (
      data: Pick<CustomViewAccess, 'customViewId' | 'userId' | 'isFavorite' | 'partnerId'> & {
        sortOrder?: number;
      },
    ) => {
      if (isNullish(data.userId)) return;

      void createCustomViewAccess({
        variables: data,
        optimisticResponse() {
          const tempId = `temp-id${new Date().toISOString()}`;

          return {
            createCustomViewAccess: [
              {
                __typename: 'CustomViewAccess',
                ...data,
                id: tempId,
                sortOrder: data.sortOrder ?? new Date().getTime(),
                customView: {
                  customViewId: data.customViewId,
                  id: data.customViewId,
                } as CustomView,
                labelAssignments: [],
                platformUser: { userId: user!.userId } as PlatformUser,
                customViewAccessId: tempId,
                customViewId: data.customViewId,
                partnerId: user!.partnerId,
                partner: { id: user!.partnerId } as Partner,
                createdAt: new Date().toISOString(),
              },
            ],
          };
        },
      });
    },
    [createCustomViewAccess, user],
  );

  const deleteViewAccess = useCallback(
    async (customViewAccessId: string) => {
      await deleteCustomViewAccess({
        variables: {
          customViewAccessId,
        },
        optimisticResponse: {
          deleteCustomViewAccess: [
            {
              id: customViewAccessId,
              sortOrder: 0,
              customView: {
                customViewId: customViewAccessId,
                id: customViewAccessId,
              } as CustomView,
              labelAssignments: [],
              platformUser: { userId: user?.userId } as PlatformUser,
              userId: user?.userId!,
              isFavorite: false,
              partnerId: user?.partnerId!,
              partner: { id: user?.partnerId } as Partner,
              customViewAccessId,
              customViewId: customViewAccessId,
              createdAt: new Date().toISOString(),
            },
          ],
        },
      });
    },
    [deleteCustomViewAccess, user],
  );

  const isLoading = loadingCreateCustomViewAccess || loadingUpdateCustomViewAccess;

  return {
    createCustomViewAccess: createWithOptimisticResponse,
    updateCustomViewAccess: updateWithOptimisticResponse,
    deleteCustomViewAccess: deleteViewAccess,
    isLoading,
  };
}
